import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Award, Users, Globe, ChevronRight, Play, Factory, Rocket, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import VideoModal from '../components/VideoModal';
import { useSiteContent } from '../context/SiteContentContext';
import { resolveImageUrl } from '../utils/urls';
import { SUBHEADING_COMPACT_PRESETS, resolveSizeClass } from '../utils/textSizes';
import SEO from '../components/SEO';

const PAGE_BANNER = 'https://jilongot.com/public/upload/20240703/240F3163430613.jpg';
const statIconMap = { Award, Users, Globe };

const _esc = (s) => (s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
const asHtml = (v) => {
  const s = (v ?? '').toString().trim();
  if (!s) return '';
  return /<\w+[^>]*>/.test(s) ? s : `<p>${_esc(s)}</p>`;
};

const Breadcrumb = ({ items }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-2 text-sm text-gray-600" data-testid="sejarah-breadcrumb">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
        {it.to ? (
          <Link to={it.to} className="hover:text-[#0a3d8f] transition-colors">{it.label}</Link>
        ) : (
          <span className="text-[#0a3d8f] font-semibold">{it.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const TIMELINE = [
  { year: '1992', title: 'Awal Berdiri', desc: 'Nanjing JILONG Optical Communication Co., Ltd. didirikan, fokus pada penelitian dan pengembangan peralatan komunikasi serat optik.' },
  { year: '2000', title: 'Ekspansi R&D', desc: 'Pusat R&D fusion splicer resmi beroperasi. JILONG mulai memproduksi fusion splicer dan cleaver berpresisi tinggi.' },
  { year: '2008', title: 'Ekspor Internasional', desc: 'Produk JILONG mulai dipasarkan ke Asia Tenggara, Timur Tengah, dan Amerika Latin.' },
  { year: '2015', title: 'Sertifikasi Global', desc: 'Memperoleh sertifikat ISO 9001, CE, FCC, dan RoHS untuk seluruh lini produk fusion splicer & OTDR.' },
  { year: '2019', title: 'Pabrik Modern 60.000 m²', desc: 'Peresmian pabrik modern dengan luas 60.000 m² dan lini produksi otomatis untuk fusion splicer trunk.' },
  { year: '2022', title: 'Fokus 5G & FTTH', desc: 'Meluncurkan seri 300T G654E dan KL-6300 OTDR yang mendukung deployment 5G backbone dan jaringan FTTH.' },
  { year: '2024', title: 'JILONG Indonesia', desc: 'Service Center resmi JILONG Indonesia dibuka di Jakarta untuk mendukung pelanggan lokal dengan garansi & sparepart.' },
];

const StrengthCard = ({ Icon, title, description }) => (
  <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all">
    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#0a3d8f] to-[#0a8f3d] text-white flex items-center justify-center mb-5 shadow-lg">
      <Icon size={26} />
    </div>
    <h4 className="text-lg font-bold text-gray-900 mb-2 font-[Montserrat]">{title}</h4>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const SejarahPage = () => {
  const { about, stats, config } = useSiteContent();
  const location = useLocation();
  const [videoOpen, setVideoOpen] = React.useState(false);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
      return () => clearTimeout(t);
    }
  }, [location.hash, about]);

  return (
    <>
      <SEO path="/sejarah" pageName="Sejarah" />
      <Header />
      <main data-testid="sejarah-page-root">
        <section className="relative h-[280px] md:h-[340px] overflow-hidden">
          <img src={PAGE_BANNER} alt="History" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a3d8f]/85 via-[#0a3d8f]/60 to-transparent" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
            <p className="text-white/80 text-sm uppercase tracking-[0.3em] mb-3">History</p>
            <h1 className="text-white text-3xl md:text-5xl font-extrabold mb-3 font-[Montserrat]">Sejarah JILONG</h1>
            <p className={`text-white/90 ${resolveSizeClass(about.subheading_size, SUBHEADING_COMPACT_PRESETS, 'lg')} max-w-2xl leading-relaxed`}>
              {about.subheading || '30+ tahun inovasi di industri komunikasi serat optik.'}
            </p>
          </div>
        </section>

        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'History' }]} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <div className="flex flex-wrap gap-3">
            {[
              { id: 'brand-introduction', label: 'Brand Introduction' },
              { id: 'history-30-year', label: '30-year History' },
              { id: 'productive-strength', label: 'Productive Strength' },
            ].map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById(s.id);
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-[#0a3d8f] hover:text-white hover:border-[#0a3d8f] transition-colors"
                data-testid={`sejarah-jump-${s.id}`}
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>

        {/* ================= 1. BRAND INTRODUCTION ================= */}
        <section id="brand-introduction" className="py-14 lg:py-20 bg-white" data-testid="sejarah-brand">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#0a3d8f] font-semibold mb-2">Brand Introduction</p>
                <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3 font-[Montserrat]">
                  {about.title || 'Tentang JILONG'}
                </h2>
                <div className="w-16 h-1 bg-[#0a3d8f] mb-5 rounded-full" />
                <div
                  className="prose prose-sm sm:prose max-w-none text-gray-700 leading-relaxed text-justify mb-6 [&_p]:mt-0 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_a]:text-[#0a3d8f] [&_a:hover]:text-[#082e6b] [&_a]:underline"
                  dangerouslySetInnerHTML={{
                    __html:
                      asHtml(about.paragraph1 || 'JILONG merupakan produsen peralatan komunikasi serat optik terkemuka yang berdiri sejak 1992. Kami menyediakan fusion splicer, OTDR, dan instrumen pengukur berkualitas tinggi untuk operator telekomunikasi, kontraktor, dan tim maintenance di seluruh dunia.') +
                      asHtml(about.paragraph2 || 'Dengan lebih dari 30 tahun pengalaman, JILONG telah menjadi mitra terpercaya bagi jaringan FTTH, backbone, dan proyek 5G di lebih dari 100 negara.'),
                  }}
                />
                <div className="flex flex-wrap gap-2">
                  {(about.badges || ['ISO 9001', 'CE Certified', '30+ Years Experience', '100+ Countries']).map((b, i) => (
                    <span key={i} className="px-4 py-1.5 rounded-full text-xs font-semibold bg-gradient-to-r from-teal-500/90 to-cyan-500/90 text-white shadow">
                      {b}
                    </span>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video group">
                  <img src={resolveImageUrl(about.video_thumbnail) || PAGE_BANNER} alt="About JILONG" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#0a3d8f]/50 to-black/50" />
                  <button
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
                    aria-label="Play brand video"
                    data-testid="sejarah-play-btn"
                    onClick={() => about.video_url && setVideoOpen(true)}
                  >
                    <Play size={28} className="text-[#0a3d8f] ml-1" fill="#0a3d8f" />
                  </button>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="text-white text-3xl font-extrabold tracking-wider opacity-90 font-[Montserrat]">JILONG</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= 2. 30-YEAR HISTORY ================= */}
        <section id="history-30-year" className="py-14 lg:py-20 bg-gradient-to-b from-blue-50/40 to-white" data-testid="sejarah-history">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#0a3d8f] font-semibold mb-2">30-year History</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3 font-[Montserrat]">Perjalanan Kami</h2>
              <div className="w-16 h-1 bg-[#0a3d8f] mx-auto mb-4 rounded-full" />
              <p className="text-gray-600 max-w-3xl mx-auto">Tiga dekade dedikasi di industri komunikasi serat optik — dari startup R&D hingga produsen global.</p>
            </div>

            {/* Timeline */}
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#0a3d8f] via-[#0a3d8f]/50 to-transparent md:-translate-x-1/2" />
              <div className="space-y-8">
                {TIMELINE.map((t, i) => (
                  <div key={t.year} className={`relative flex md:items-center gap-6 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:block flex-1" />
                    <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 w-4 h-4 rounded-full bg-[#0a3d8f] border-4 border-white shadow-md z-10 mt-1.5 md:mt-0" />
                    <div className="ml-12 md:ml-0 flex-1 bg-white p-5 rounded-xl border border-gray-100 shadow-md hover:shadow-lg transition-shadow" data-testid={`timeline-${t.year}`}>
                      <div className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-[#0a3d8f] text-white mb-2">{t.year}</div>
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5 font-[Montserrat]">{t.title}</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">{t.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= 3. PRODUCTIVE STRENGTH ================= */}
        <section id="productive-strength" className="py-14 lg:py-20 bg-white" data-testid="sejarah-productive">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.3em] text-[#0a3d8f] font-semibold mb-2">Productive Strength</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3 font-[Montserrat]">Kekuatan Produksi</h2>
              <div className="w-16 h-1 bg-[#0a3d8f] mx-auto mb-4 rounded-full" />
              <p className="text-gray-600 max-w-3xl mx-auto">Fasilitas manufaktur, R&D, dan quality control kelas dunia yang mendukung standar produk JILONG.</p>
            </div>

            {/* Stats */}
            <div className="grid sm:grid-cols-3 gap-6 mb-12">
              {stats.map((stat) => {
                const Icon = statIconMap[stat.icon] || Award;
                return (
                  <div key={stat.id || stat.label} className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-md border border-gray-100" data-testid={`sejarah-stat-${stat.label}`}>
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-14 h-14 rounded-lg bg-[#0a3d8f]/10 text-[#0a3d8f] flex items-center justify-center">
                        <Icon size={26} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-extrabold text-[#0a3d8f] font-[Montserrat]">{stat.value}</span>
                          <span className="text-2xl font-bold text-[#e60012]">{stat.suffix}</span>
                        </div>
                        <h4 className="text-base font-bold text-gray-900 mb-2">{stat.label}</h4>
                        <p className="text-xs text-gray-600 leading-relaxed">{stat.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Strength cards */}
            <div className="grid md:grid-cols-3 gap-6">
              <StrengthCard
                Icon={Factory}
                title="Pabrik Modern 60,000 m²"
                description="Fasilitas manufaktur otomatis di Nanjing dengan lini produksi fusion splicer trunk berkecepatan tinggi dan cleanroom Class-1000."
              />
              <StrengthCard
                Icon={Rocket}
                title="Pusat R&D Bersertifikat"
                description="Tim R&D dengan 200+ engineer dan lebih dari 150 paten teknis pada fusion splicing, imaging, dan optical measurement."
              />
              <StrengthCard
                Icon={Sparkles}
                title="Quality Control 5-Level"
                description="Setiap produk melewati 5 tahap QC — dari komponen incoming, sub-assembly, final assembly, burn-in, hingga field simulation."
              />
            </div>
          </div>
        </section>

        <section className="py-14 bg-gradient-to-r from-[#0a3d8f] to-[#082e6b] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-extrabold mb-3 font-[Montserrat]">Bagian dari Perjalanan JILONG</h3>
              <p className="text-white/85 leading-relaxed">Diskusikan kebutuhan proyek fiber optic Anda dengan tim JILONG Indonesia — kami siap memberikan solusi terbaik.</p>
            </div>
            <div className="flex flex-wrap gap-3 md:justify-end">
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 bg-white text-[#0a3d8f] hover:bg-white/90 px-5 py-3 rounded-md text-sm font-bold"
                data-testid="sejarah-cta-contact"
              >
                Hubungi Tim Kami
              </a>
              <Link to="/produk" className="inline-flex items-center gap-2 border border-white/70 hover:bg-white/10 px-5 py-3 rounded-md text-sm font-bold" data-testid="sejarah-cta-products">
                Jelajahi Produk
              </Link>
            </div>
          </div>
        </section>

        <ContactSection />

        <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} url={about.video_url} title="JILONG Brand Video" />
      </main>
      <Footer />
    </>
  );
};

export default SejarahPage;
